import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Award, CalendarDays, CheckCheck, ClipboardList, Clock, FileText, Smartphone, UserPlus,
} from 'lucide-react';
import api from '../../utils/axios';
import useCardData from './useCardData';
import {
  DashboardCard, CardLoading, CardError, CardEmpty,
} from './DashboardCard';
import { formatDay, formatDayRange, fullName } from './format';

const KIND_STYLE = {
  leave: { icon: CalendarDays, className: 'bg-primary-50 text-primary-600' },
  attendance: { icon: Clock, className: 'bg-warning-50 text-warning-600' },
  appraisal: { icon: Award, className: 'bg-success-50 text-success-600' },
  onboarding: { icon: UserPlus, className: 'bg-primary-50 text-primary-600' },
  device: { icon: Smartphone, className: 'bg-gray-100 text-gray-600' },
  document: { icon: FileText, className: 'bg-info-50 text-info-600' },
};

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// Rows listed per queue. The rest of a queue is one visible "+N more" link under it, so the
// card never hides tasks in a scroll box behind a count that does not match the rows.
export const ROWS_PER_QUEUE = 3;

/**
 * Which task queues the signed-in user works, each from the permission that lets them act
 * on it and the existing list endpoint for it. Every source resolves to
 * `{ total, items: [{ id, kind, title, detail, to }] }`; a summary source (the attendance
 * queues) lists one item standing for the whole queue.
 */
export function taskSources(auth) {
  const { hasPermission, hasAnyResourceAction } = auth;
  const sources = [];

  if (hasPermission('leaves', 'approve')) {
    sources.push({
      key: 'leaves',
      load: async () => {
        const res = await api.get('/api/leaves', { params: { awaiting: 'me', status: 'pending', limit: ROWS_PER_QUEUE } });
        return {
          total: res.data.total ?? res.data.data.length,
          items: res.data.data.map((leave) => ({
            id: `leave-${leave.id}`,
            kind: 'leave',
            title: `${fullName(leave)} requested ${leave.leave_type_name || 'leave'}`,
            detail: formatDayRange(leave.start_date, leave.end_date),
            to: `/leaves/${leave.id}`,
          })),
          more: { label: 'All leave requests', to: '/leaves' },
        };
      },
    });
  }

  // The queues are department- or company-scoped; a read_own holder is refused (403).
  if (hasPermission('timesheet', 'acknowledge') && hasAnyResourceAction('timesheet', ['read_all', 'read_department'])) {
    sources.push({
      key: 'attendance',
      load: async () => {
        const [amendments, wfh] = await Promise.all([
          api.get('/api/timesheet/amendments', { params: { status: 'pending', limit: 1 } }),
          api.get('/api/timesheet/wfh', { params: { status: 'pending', limit: 1 } }),
        ]);
        const items = [];
        const corrections = amendments.data.total || 0;
        const decisions = wfh.data.total || 0;
        if (corrections > 0) {
          items.push({
            id: 'attendance-amendments',
            kind: 'attendance',
            title: `${plural(corrections, 'attendance correction', 'attendance corrections')} to acknowledge`,
            detail: 'Attendance approvals',
            to: '/attendance?tab=approvals',
          });
        }
        if (decisions > 0) {
          items.push({
            id: 'attendance-wfh',
            kind: 'attendance',
            title: `${plural(decisions, 'work-from-home or office request', 'work-from-home or office requests')} to decide`,
            detail: 'Attendance approvals',
            to: '/attendance?tab=approvals',
          });
        }
        return { total: corrections + decisions, items };
      },
    });
  }

  // Appraisals due: for whoever records them (HR, a manager for their reports).
  if (hasAnyResourceAction('appraisals', ['create', 'update_all', 'update_reports'])
    && hasAnyResourceAction('appraisals', ['read_all', 'read_department', 'read_reports'])) {
    sources.push({
      key: 'appraisals',
      load: async () => {
        const [overdue, upcoming] = await Promise.all([
          api.get('/api/appraisals/overdue/list'),
          api.get('/api/appraisals/upcoming/list', { params: { days: 7 } }),
        ]);
        const due = [
          ...overdue.data.data.map((appraisal) => ({ appraisal, overdue: true })),
          ...upcoming.data.data
            .filter((appraisal) => appraisal.status !== 'completed')
            .map((appraisal) => ({ appraisal, overdue: false })),
        ];
        return {
          total: due.length,
          items: due.slice(0, 5).map(({ appraisal, overdue: late }) => ({
            id: `appraisal-${appraisal.id}`,
            kind: 'appraisal',
            title: `Appraisal ${late ? 'overdue' : 'due'} for ${fullName(appraisal)}`,
            detail: `${late ? 'Was due' : 'Due'} ${formatDay(appraisal.next_appraisal_date)}`,
            to: `/appraisals/${appraisal.id}`,
          })),
          more: { label: 'All appraisals', to: '/appraisals' },
        };
      },
    });
  }

  if (hasPermission('onboarding', 'review')) {
    sources.push({
      key: 'onboarding',
      load: async () => {
        const res = await api.get('/api/onboarding', { params: { status: 'pending_review', limit: ROWS_PER_QUEUE } });
        return {
          total: res.data.total ?? res.data.data.length,
          items: res.data.data.map((request) => ({
            id: `onboarding-${request.id}`,
            kind: 'onboarding',
            title: `Review onboarding for ${fullName(request) || 'a new joiner'}`,
            detail: request.department_name || 'Onboarding request',
            to: `/onboarding/requests/${request.id}`,
          })),
          more: { label: 'All onboarding requests', to: '/onboarding/requests' },
        };
      },
    });
  }

  // Uploaded documents waiting for HR approval (ALEE-2). Each opens the employee's profile,
  // where the document is approved or rejected; there is no documents page.
  if (hasPermission('documents', 'approve')) {
    sources.push({
      key: 'documents',
      load: async () => {
        const res = await api.get('/api/documents', { params: { status: 'pending', limit: ROWS_PER_QUEUE } });
        return {
          total: res.data.total ?? res.data.data.length,
          items: res.data.data.map((doc) => ({
            id: `document-${doc.id}`,
            kind: 'document',
            title: `Review ${doc.document_type} from ${fullName(doc) || 'an employee'}`,
            detail: `Uploaded ${formatDay(doc.upload_date)}`,
            to: `/employees/${doc.owner_employee_id}#documents`,
          })),
          more: { label: 'All employees', to: '/employees' },
        };
      },
    });
  }

  if (hasPermission('devices', 'manage')) {
    sources.push({
      key: 'devices',
      load: async () => {
        const res = await api.get('/api/mobile/devices', { params: { status: 'pending' } });
        const devices = res.data.data.devices || [];
        return {
          total: devices.length,
          items: devices.slice(0, 5).map((device) => ({
            id: `device-${device.id}`,
            kind: 'device',
            title: `Approve a phone for ${fullName(device) || device.username}`,
            detail: device.model || device.name || 'Mobile device',
            to: '/mobile-devices',
          })),
          more: { label: 'All devices', to: '/mobile-devices' },
        };
      },
    });
  }

  return sources;
}

/**
 * Settles every source on its own, so one failing queue still shows the others. Throws
 * only when every source failed.
 */
async function loadTasks(sources) {
  const settled = await Promise.allSettled(sources.map((source) => source.load()));
  const loaded = settled
    .map((result, index) => (result.status === 'fulfilled' ? { key: sources[index].key, ...result.value } : null))
    .filter(Boolean);
  if (loaded.length === 0 && sources.length > 0) throw settled[0].reason;
  return {
    total: loaded.reduce((sum, source) => sum + source.total, 0),
    groups: loaded.filter((source) => source.items.length > 0),
    failed: settled.length - loaded.length,
  };
}

const TaskRow = ({ task }) => {
  const style = KIND_STYLE[task.kind];
  const Icon = style.icon;
  return (
    <li>
      <Link
        to={task.to}
        data-testid="dashboard-task-row"
        className="group flex items-center gap-3 rounded-lg px-2 py-2.5 -mx-2 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <span className={`flex-shrink-0 p-2 rounded-lg ${style.className}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-medium text-gray-900 break-words">{task.title}</span>
          <span className="block text-xs text-gray-500 truncate">{task.detail}</span>
        </span>
        <ArrowRight className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-primary-600" aria-hidden="true" />
      </Link>
    </li>
  );
};

/**
 * Items waiting on the signed-in user. Rendered only for someone who works at least one
 * queue (the Dashboard asks taskSources first); a plain employee has none.
 */
const PendingTasksCard = ({ sources, className = '' }) => {
  const sourceKeys = sources.map((source) => source.key).join(',');
  const tasks = useCardData(() => loadTasks(sources), [sourceKeys]);

  return (
    <DashboardCard
      data-testid="dashboard-pending-tasks"
      title="Pending tasks"
      icon={ClipboardList}
      // A queue that failed is not in the total, so the count says it is a lower bound.
      count={tasks.data ? `${tasks.data.total}${tasks.data.failed > 0 ? '+' : ''}` : undefined}
      className={className}
    >
      {tasks.loading && <CardLoading label="Loading pending tasks" />}
      {tasks.error && (
        <CardError
          data-testid="dashboard-pending-tasks-error"
          message="Could not load your pending tasks."
          onRetry={tasks.reload}
        />
      )}
      {tasks.data && tasks.data.groups.length === 0 && tasks.data.failed === 0 && (
        <CardEmpty icon={CheckCheck} data-testid="dashboard-pending-tasks-empty">
          You are all caught up.
        </CardEmpty>
      )}
      {tasks.data && tasks.data.groups.length > 0 && (
        <div className="-my-1 divide-y divide-gray-100">
          {tasks.data.groups.map((group) => {
            // A summary queue (attendance) lists one row per kind and has no "more".
            const shown = group.more ? group.items.slice(0, ROWS_PER_QUEUE) : group.items;
            const hidden = group.more ? group.total - shown.length : 0;
            return (
              <div key={group.key} className="py-1">
                <ul>
                  {shown.map((task) => <TaskRow key={task.id} task={task} />)}
                </ul>
                {hidden > 0 && (
                  <Link
                    to={group.more.to}
                    data-testid={`dashboard-pending-tasks-more-${group.key}`}
                    className="ml-11 inline-flex items-center pb-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    {`+${hidden} more · ${group.more.label}`}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
      {tasks.data && tasks.data.failed > 0 && (
        <p role="alert" className="mt-3 text-xs text-danger-700">
          Some tasks could not be loaded.{' '}
          <button type="button" onClick={tasks.reload} className="font-medium underline hover:text-danger-800">
            Try again
          </button>
        </p>
      )}
    </DashboardCard>
  );
};

export default PendingTasksCard;
